<template>
  <transition name="ai-panel-slide">
    <aside
      v-if="panelOpen"
      class="ai-panel ai-panel--cli"
      :class="{ 'ai-panel--dark': darkMode, 'ai-panel--max': maximized }"
      role="complementary"
      aria-label="RingbaX AI agent"
    >
      <header class="ai-panel__header">
        <div class="ai-panel__brand">
          <span class="ai-panel__logo">
            <span class="ai-panel__logo-pulse" />
          </span>
          <div class="ai-panel__brand-text">
            <div class="ai-panel__title">
              RingbaX Copilot
            </div>
            <div class="ai-panel__subtitle">
              <span
                class="ai-panel__status-dot"
                :class="{ 'ai-panel__status-dot--active': isStreaming }"
              />
              {{ statusText }}
              <span class="ai-panel__badge">Beta</span>
            </div>
          </div>
        </div>
        <div class="ai-panel__actions">
          <button
            type="button"
            class="ai-panel__icon-btn"
            title="Home — start screen"
            aria-label="Home"
            @click="startNewSession"
          >
            <CIcon name="cil-home" />
          </button>
          <button
            type="button"
            class="ai-panel__icon-btn"
            :class="{ 'ai-panel__icon-btn--active': showSessions }"
            title="Chat history"
            aria-label="Chat history"
            @click="toggleSessions"
          >
            <CIcon name="cil-history" />
          </button>
          <button
            v-if="messages.length"
            type="button"
            class="ai-panel__icon-btn"
            title="New chat (saves the current one to history)"
            aria-label="New chat"
            @click="startNewSession"
          >
            <CIcon name="cil-plus" />
          </button>
          <button
            type="button"
            class="ai-panel__icon-btn ai-panel__icon-btn--maximize"
            :title="maximized ? 'Restore panel size' : 'Maximize to full width'"
            @click="maximized = !maximized"
          >
            <CIcon :name="maximized ? 'cil-fullscreen-exit' : 'cil-fullscreen'" />
          </button>
          <button
            type="button"
            class="ai-panel__icon-btn"
            title="Close (Esc)"
            @click="closePanel"
          >
            <CIcon name="cil-x" />
          </button>
        </div>
      </header>

      <div
        ref="scroll"
        class="ai-panel__messages"
        @scroll.passive="onMessagesScroll"
      >
        <div
          v-if="!messages.length"
          class="ai-panel__empty"
        >
          <div class="ai-panel__empty-glow" />
          <h3 class="ai-panel__empty-title">
            Hey, I'm your RingbaX assistant
          </h3>
          <p class="ai-panel__empty-text">
            I help you find answers fast. Ask in plain English and I'll pull the data, explain the numbers, and help you troubleshoot.
          </p>
          <div class="ai-panel__empty-notes">
            <div class="ai-panel__empty-note">
              <span class="ai-panel__empty-note-icon">🧾</span>
              <span class="ai-panel__empty-note-text"><strong>Billing pilot.</strong> Right now I cover billing: invoices, balances, payments, and disputed calls. More areas are on the way.</span>
            </div>
            <div class="ai-panel__empty-note">
              <span class="ai-panel__empty-note-icon">🔒</span>
              <span class="ai-panel__empty-note-text"><strong>Read-only.</strong> I can look things up, but I can't change anything. That's by design, for security.</span>
            </div>
          </div>
          <div class="ai-panel__suggest-label">
            Ready to try? Ask me something
          </div>
          <div class="ai-panel__suggestions">
            <button
              v-for="(suggestion, idx) in suggestions"
              :key="idx"
              type="button"
              class="ai-panel__suggestion"
              @click="useSuggestion(suggestion)"
            >
              <span class="ai-panel__suggestion-icon">{{ suggestion.icon }}</span>
              <span class="ai-panel__suggestion-body">
                <span class="ai-panel__suggestion-text">{{ suggestion.text }}</span>
                <span class="ai-panel__suggestion-hint">{{ suggestion.hint }}</span>
              </span>
            </button>
          </div>
        </div>

        <div
          v-else
          class="ai-panel__thread"
        >
          <AiAgentCliMessage
            v-for="message in messages"
            :key="message.id"
            :message="message"
          />
        </div>
      </div>

      <footer class="ai-panel__composer">
        <div
          v-if="queuedPrompts.length"
          class="ai-panel__queue"
        >
          <div class="ai-panel__queue-head">
            {{ queuedPrompts.length }} queued, going out together when this turn ends
          </div>
          <div
            v-for="(text, idx) in queuedPrompts"
            :key="idx"
            class="ai-panel__queue-item"
          >
            <span class="ai-panel__queue-index">{{ idx + 1 }}</span>
            <span class="ai-panel__queue-text">{{ text }}</span>
            <button
              type="button"
              class="ai-panel__queue-drop"
              title="Remove from queue"
              @click="removeQueuedPrompt(idx)"
            >
              <CIcon
                name="cil-x"
                size="sm"
              />
            </button>
          </div>
        </div>
        <div
          v-if="isStreaming"
          class="ai-panel__streaming-bar"
        >
          <span class="ai-panel__streaming-text">Working on it…</span>
          <button
            type="button"
            class="ai-panel__stop-btn"
            @click="stopStreaming"
          >
            <CIcon
              name="cil-x-circle"
              size="sm"
            />
            Stop{{ queuedPrompts.length ? ' and clear queue' : '' }}
          </button>
        </div>
        <div
          v-if="mentionOpen"
          class="ai-panel__mention"
        >
          <div class="ai-panel__mention-head">
            Accounts matching "{{ mentionQuery }}"
          </div>
          <div
            v-if="mention.loading"
            class="ai-panel__mention-empty"
          >
            Searching…
          </div>
          <template v-else>
            <button
              v-for="(item, idx) in mention.results"
              :key="item.id"
              type="button"
              class="ai-panel__mention-item"
              :class="{ 'ai-panel__mention-item--active': idx === mention.selectedIndex }"
              @mousedown.prevent
              @mousemove="mention.selectedIndex = idx"
              @click="applyMention(item)"
            >
              <span
                class="ai-panel__mention-type"
                :class="`ai-panel__mention-type--${item.type}`"
              >{{ item.type }}</span>
              <span class="ai-panel__mention-name">{{ item.name }}</span>
              <span class="ai-panel__mention-id">{{ item.id }}</span>
            </button>
            <div
              v-if="!mention.results.length"
              class="ai-panel__mention-empty"
            >
              No accounts match "{{ mentionQuery }}"
            </div>
          </template>
        </div>
        <div class="ai-panel__input-wrap">
          <textarea
            ref="input"
            v-model="draft"
            class="ai-panel__input"
            rows="1"
            :placeholder="placeholder"
            @keydown.enter.exact.prevent="onEnterKey"
            @keydown.shift.enter.exact="onShiftEnter"
            @keydown.up="onArrowUp"
            @keydown.down="onArrowDown"
            @keydown.tab="onTabKey"
            @keyup.left="updateMention"
            @keyup.right="updateMention"
            @click="updateMention"
            @blur="closeMention"
            @input="onInput"
          />
          <button
            type="button"
            class="ai-panel__send-btn"
            :disabled="!canSubmit"
            :title="sendTitle"
            @click="submit"
          >
            <CIcon
              v-if="!isStreaming || draft.trim()"
              name="cil-arrow-thick-top"
            />
            <span
              v-else
              class="ai-panel__send-spinner"
            />
          </button>
        </div>
        <div class="ai-panel__hint">
          <kbd>@</kbd> account by name · <kbd>Enter</kbd> to send · <kbd>Shift</kbd>+<kbd>Enter</kbd> newline · <kbd>↑</kbd><kbd>↓</kbd> history · <kbd>Esc</kbd> close
        </div>
      </footer>

      <div
        v-if="showSessions"
        class="ai-panel__history"
      >
        <div class="ai-panel__history-head">
          <span class="ai-panel__history-title">Chat history</span>
          <div class="ai-panel__history-head-actions">
            <button
              type="button"
              class="ai-panel__history-new"
              @click="startNewSession"
            >
              <CIcon
                name="cil-plus"
                size="sm"
              />
              New chat
            </button>
            <button
              type="button"
              class="ai-panel__icon-btn"
              title="Close history"
              aria-label="Close history"
              @click="showSessions = false"
            >
              <CIcon name="cil-x" />
            </button>
          </div>
        </div>
        <div class="ai-panel__history-list">
          <p
            v-if="!sortedSessions.length"
            class="ai-panel__history-empty"
          >
            No saved chats yet. Conversations you have are saved here so you can pick them back up later.
          </p>
          <div
            v-for="session in sortedSessions"
            :key="session.id"
            class="ai-panel__history-item"
            :class="{ 'ai-panel__history-item--active': session.id === currentSessionId }"
          >
            <button
              type="button"
              class="ai-panel__history-open"
              @click="openSession(session.id)"
            >
              <span class="ai-panel__history-item-title">{{ session.title }}</span>
              <span class="ai-panel__history-item-meta">
                {{ formatSessionTime(session.updatedAt) }} · {{ session.messageCount }} {{ session.messageCount === 1 ? 'message' : 'messages' }}
              </span>
            </button>
            <button
              type="button"
              class="ai-panel__history-del"
              title="Delete chat"
              aria-label="Delete chat"
              @click="removeSession(session.id)"
            >
              <CIcon
                name="cil-trash"
                size="sm"
              />
            </button>
          </div>
        </div>
      </div>
    </aside>
  </transition>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import AiAgentCliMessage from './AiAgentCliMessage.vue'
import AccountsAPI from '@/api/accounts.api'
import { MAX_QUEUED_PROMPTS } from '@/store/modules/ai-agent.store'

// One suggestion per thing the copilot can already answer today (billing
// agent tools + campaign specialist). Prompts need an account id; typing @
// in the composer resolves an account name to its id via admin search.
const SUGGESTIONS = [
  { icon: '🧾', text: 'Show the latest invoices for a buyer', hint: 'Type @ to pick the buyer by name' },
  { icon: '💰', text: "What's the outstanding balance for an account?", hint: 'Works for buyers and sellers' },
  { icon: '📊', text: 'Why did the balance change last week?', hint: 'Ledger history between two dates' },
  { icon: '⚖️', text: 'Which calls were disputed this month?', hint: 'Disputed calls with fees and adjustments' },
  { icon: '🎯', text: 'List buyer campaigns and their status', hint: 'Pause state and schedules' }
]

const MENTION_MIN_QUERY = 1
const MENTION_MAX_QUERY = 40
const MENTION_DEBOUNCE_MS = 350
const MENTION_MAX_RESULTS = 8

export default {
  name: 'AiAgentPanel',

  components: { AiAgentCliMessage },

  data() {
    return {
      draft: '',
      suggestions: SUGGESTIONS,
      isNearBottom: true,
      maximized: false,
      showSessions: false,
      // Shell-style history navigation. historyIndex === null means "not
      // browsing history"; stashedDraft holds whatever was being typed before
      // the user pressed ArrowUp, so ArrowDown can restore it.
      historyIndex: null,
      stashedDraft: '',
      // @-mention account lookup. start is the index of the '@' in the draft;
      // the query is whatever sits between it and the caret.
      mention: {
        active: false,
        query: '',
        start: -1,
        results: [],
        loading: false,
        selectedIndex: 0
      },
      mentionDebounce: null,
      mentionAbort: null
    }
  },

  computed: {
    ...mapState('aiAgent', ['panelOpen', 'messages', 'isStreaming', 'promptHistory', 'currentSessionId', 'queuedPrompts']),
    ...mapState(['darkMode']),
    ...mapGetters('aiAgent', ['lastMessageSignature', 'sortedSessions']),
    queueFull() {
      return this.queuedPrompts.length >= MAX_QUEUED_PROMPTS
    },
    canSubmit() {
      return Boolean(this.draft.trim()) && !(this.isStreaming && this.queueFull)
    },
    placeholder() {
      if (this.queueFull) {
        return `Queue is full at ${MAX_QUEUED_PROMPTS}. Wait for these to run`
      }

      if (this.isStreaming) {
        return 'Type your next prompt, it runs when this one finishes'
      }

      return 'Ask about invoices, balances, disputes… @ adds an account by name'
    },
    sendTitle() {
      if (!this.canSubmit) {
        return this.queueFull ? 'Queue is full' : 'Type a prompt'
      }

      return this.isStreaming ? 'Add to queue (Enter)' : 'Send (Enter)'
    },
    mentionQuery() {
      return this.mention.query.trim()
    },
    mentionOpen() {
      return this.mention.active && this.mentionQuery.length >= MENTION_MIN_QUERY
    },
    statusText() {
      if (this.isStreaming) {
        return 'Thinking…'
      }

      if (this.messages.length) {
        return 'Idle'
      }

      return 'Ready'
    }
  },

  watch: {
    panelOpen(open) {
      if (open) {
        // Always reopen onto the conversation, not a stale history view.
        this.showSessions = false
        this.$nextTick(() => {
          this.$refs.input?.focus()
          this.scrollToBottomIfFollowing(true)
        })
      }
    },
    // Watch a cheap O(1) signature instead of running a deep watcher on the
    // whole `messages` array. Recomputes once per mutation, not per nested
    // change. Only auto-scrolls when the user was already near the bottom —
    // if they've scrolled up to read history, we leave them alone.
    lastMessageSignature() {
      this.$nextTick(() => this.scrollToBottomIfFollowing(false))
    }
  },

  mounted() {
    window.addEventListener('keydown', this.onGlobalKey)
  },

  beforeDestroy() {
    window.removeEventListener('keydown', this.onGlobalKey)
    clearTimeout(this.mentionDebounce)
    if (this.mentionAbort) {
      this.mentionAbort.abort()
    }
  },

  methods: {
    ...mapActions('aiAgent', [
      'submitPrompt',
      'removeQueuedPrompt',
      'closePanel',
      'clearChat',
      'stopStreaming',
      'loadSession',
      'deleteSession'
    ]),

    toggleSessions() {
      this.showSessions = !this.showSessions
    },

    openSession(id) {
      this.loadSession(id)
      this.showSessions = false
      this.isNearBottom = true
      this.$nextTick(() => this.scrollToBottomIfFollowing(true))
    },

    removeSession(id) {
      this.deleteSession(id)
    },

    startNewSession() {
      this.clearChat()
      this.showSessions = false
      this.$nextTick(() => this.$refs.input?.focus())
    },

    formatSessionTime(ts) {
      if (!ts) {
        return ''
      }

      const d = new Date(ts)
      const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      if (d.toDateString() === new Date().toDateString()) {
        return time
      }

      return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${time}`
    },

    onGlobalKey(event) {
      if (!this.panelOpen) {
        return
      }

      if (event.key === 'Escape') {
        // Step out of the innermost layer first: mention popover, then the
        // history overlay, then the panel itself.
        if (this.mentionOpen) {
          this.closeMention()
        } else if (this.showSessions) {
          this.showSessions = false
        } else {
          this.closePanel()
        }
      }
    },

    onShiftEnter() {
      // Allow newline; just keep textarea expanding
      this.$nextTick(this.autosize)
    },

    onInput() {
      // Any manual edit exits history browsing, so the next ArrowUp starts a
      // fresh walk from the newest entry.
      this.historyIndex = null
      this.autosize()
      this.updateMention()
    },

    // True when the caret sits on the first visual line — only then does
    // ArrowUp recall history instead of moving the cursor up a line.
    caretOnFirstLine() {
      const el = this.$refs.input
      if (!el) {
        return true
      }

      return el.value.slice(0, el.selectionStart).indexOf('\n') === -1
    },

    caretOnLastLine() {
      const el = this.$refs.input
      if (!el) {
        return true
      }

      return el.value.slice(el.selectionStart).indexOf('\n') === -1
    },

    onArrowUp(event) {
      if (this.mentionOpen && this.mention.results.length) {
        event.preventDefault()
        const len = this.mention.results.length
        this.mention.selectedIndex = (this.mention.selectedIndex + len - 1) % len
        return
      }

      if (!this.promptHistory.length || !this.caretOnFirstLine()) {
        return
      }

      event.preventDefault()

      if (this.historyIndex === null) {
        this.stashedDraft = this.draft
        this.historyIndex = this.promptHistory.length - 1
      } else if (this.historyIndex > 0) {
        this.historyIndex--
      } else {
        return
      }

      this.applyHistoryEntry(this.promptHistory[this.historyIndex])
    },

    onArrowDown(event) {
      if (this.mentionOpen && this.mention.results.length) {
        event.preventDefault()
        this.mention.selectedIndex = (this.mention.selectedIndex + 1) % this.mention.results.length
        return
      }

      // Only intercept when actively browsing history and at the last line.
      if (this.historyIndex === null || !this.caretOnLastLine()) {
        return
      }

      event.preventDefault()

      if (this.historyIndex < this.promptHistory.length - 1) {
        this.historyIndex++
        this.applyHistoryEntry(this.promptHistory[this.historyIndex])
      } else {
        // Stepped past the newest entry — restore the in-progress draft.
        this.historyIndex = null
        this.applyHistoryEntry(this.stashedDraft)
        this.stashedDraft = ''
      }
    },

    onEnterKey() {
      if (this.mentionOpen && !this.mention.loading && this.mention.results.length) {
        this.applyMention(this.mention.results[this.mention.selectedIndex])
        return
      }

      this.submit()
    },

    onTabKey(event) {
      if (this.mentionOpen && !this.mention.loading && this.mention.results.length) {
        event.preventDefault()
        this.applyMention(this.mention.results[this.mention.selectedIndex])
      }
    },

    // Track an "@query" between the last @ and the caret. The @ must start a
    // word so emails like a@b.com never trigger the popover.
    updateMention() {
      const el = this.$refs.input
      if (!el) {
        this.closeMention()
        return
      }

      const upToCaret = this.draft.slice(0, el.selectionStart)
      const at = upToCaret.lastIndexOf('@')
      const validStart = at !== -1 && (at === 0 || /\s/.test(upToCaret[at - 1]))
      const query = validStart ? upToCaret.slice(at + 1) : ''

      if (!validStart || query.includes('\n') || query.length > MENTION_MAX_QUERY) {
        this.closeMention()
        return
      }

      const unchanged = this.mention.active && this.mention.query === query
      this.mention.active = true
      this.mention.start = at
      this.mention.query = query
      if (unchanged) {
        return
      }

      clearTimeout(this.mentionDebounce)
      const trimmed = query.trim()
      if (trimmed.length < MENTION_MIN_QUERY) {
        this.mention.results = []
        this.mention.loading = false
        return
      }

      this.mention.loading = true
      this.mentionDebounce = setTimeout(() => this.searchMentionAccounts(trimmed), MENTION_DEBOUNCE_MS)
    },

    async searchMentionAccounts(query) {
      if (this.mentionAbort) {
        this.mentionAbort.abort()
      }

      const controller = new AbortController()
      this.mentionAbort = controller
      const { signal } = controller

      const [buyers, sellers] = await Promise.all([
        AccountsAPI.searchBuyerAccounts(query, { signal }).catch(() => null),
        AccountsAPI.searchSellerAccounts(query, { signal }).catch(() => null)
      ])

      // A newer search (or closeMention) superseded this one — drop it.
      if (signal.aborted) {
        return
      }

      const pick = (response, type) => (response?.data || [])
        .filter(a => a && a.id && a.name)
        .map(a => ({ id: a.id, name: a.name, type }))

      this.mention.results = [...pick(buyers, 'buyer'), ...pick(sellers, 'seller')].slice(0, MENTION_MAX_RESULTS)
      this.mention.selectedIndex = 0
      this.mention.loading = false
    },

    applyMention(account) {
      const el = this.$refs.input
      const caret = el ? el.selectionStart : this.draft.length
      const before = this.draft.slice(0, this.mention.start)
      const after = this.draft.slice(caret)
      // Insert name + id so the prompt reads naturally and the copilot still
      // gets the exact account id it needs.
      const inserted = `${account.name} (${account.id})` + (after.startsWith(' ') ? '' : ' ')
      this.draft = before + inserted + after
      this.closeMention()

      const caretTo = (before + inserted).length
      this.$nextTick(() => {
        this.autosize()
        if (el) {
          el.focus()
          el.setSelectionRange(caretTo, caretTo)
        }
      })
    },

    closeMention() {
      clearTimeout(this.mentionDebounce)
      if (this.mentionAbort) {
        this.mentionAbort.abort()
        this.mentionAbort = null
      }

      this.mention.active = false
      this.mention.query = ''
      this.mention.start = -1
      this.mention.results = []
      this.mention.loading = false
      this.mention.selectedIndex = 0
    },

    applyHistoryEntry(value) {
      this.closeMention()
      this.draft = value
      this.$nextTick(() => {
        this.autosize()
        const el = this.$refs.input
        if (el) {
          const end = el.value.length
          el.setSelectionRange(end, end)
        }
      })
    },

    onMessagesScroll() {
      const el = this.$refs.scroll
      if (!el) {
        return
      }

      // Treat anything within 80px of the bottom as "following". Captures
      // user intent: scrolled up to read = stay there; sitting at bottom =
      // auto-follow new tokens.
      const gap = el.scrollHeight - el.scrollTop - el.clientHeight
      this.isNearBottom = gap < 80
    },

    autosize() {
      const el = this.$refs.input
      if (!el) {
        return
      }

      el.style.height = 'auto'
      el.style.height = Math.min(el.scrollHeight, 160) + 'px'
    },

    submit() {
      if (!this.canSubmit) {
        return
      }

      const text = this.draft
      this.draft = ''
      this.historyIndex = null
      this.stashedDraft = ''
      this.closeMention()
      this.autosize()
      this.isNearBottom = true
      this.submitPrompt(text)
    },

    useSuggestion(suggestion) {
      // Drop the suggestion into the composer and send it.
      this.draft = suggestion.text
      this.$nextTick(() => {
        this.autosize()
        this.submit()
      })
    },

    scrollToBottomIfFollowing(force) {
      const el = this.$refs.scroll
      if (!el) {
        return
      }

      if (force || this.isNearBottom) {
        el.scrollTop = el.scrollHeight
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.ai-panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 440px;
  max-width: 100vw;
  background: #ffffff;
  border-left: 1px solid #e9ecf3;
  box-shadow: -16px 0 48px rgba(30, 44, 80, 0.12), -4px 0 16px rgba(30, 44, 80, 0.06);
  z-index: 1050;
  display: flex;
  flex-direction: column;
  font-family: inherit;
  transition: width 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: 600px) {
    width: 100vw;
  }

  &--cli {
    width: 720px;

    @media (max-width: 900px) {
      width: 100vw;
    }
  }

  // Maximize: take the full viewport like a real terminal. Wins over both the
  // chat and cli widths because it is declared after them.
  &--max {
    width: 100vw;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    border-bottom: 1px solid #e9ecf3;
    background: linear-gradient(180deg, #f6f8fd 0%, #ffffff 100%);
    flex-shrink: 0;
  }

  &__brand {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1 1 auto;
    min-width: 0;
  }

  &__brand-text {
    min-width: 0;
  }

  &__logo {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: linear-gradient(135deg, #5b73e8 0%, #8b5cf6 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    box-shadow: 0 2px 8px rgba(91, 115, 232, 0.35);
  }

  &__logo-pulse {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 0 12px rgba(255, 255, 255, 0.8);
    animation: ai-pulse 2s ease-in-out infinite;
  }

  &__title {
    font-size: 0.95rem;
    font-weight: 700;
    color: #1e2b45;
    line-height: 1.1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__subtitle {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.72rem;
    color: #96a5be;
    margin-top: 3px;
    min-width: 0;
  }

  &__status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #96a5be;

    &--active {
      background: #5b73e8;
      box-shadow: 0 0 6px rgba(91, 115, 232, 0.7);
      animation: ai-pulse 1.2s ease-in-out infinite;
    }
  }

  &__badge {
    background: linear-gradient(135deg, #5b73e8 0%, #8b5cf6 100%);
    color: #fff;
    font-size: 0.6rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 1px 6px;
    border-radius: 999px;
    margin-left: 4px;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
  }

  &__icon-btn {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: transparent;
    border: 0;
    color: #5a6782;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s ease, color 0.15s ease;

    &:hover { background: #f2f4f9; color: #1e2b45; }
    &--active { background: #eef0fd; color: #3858e7; }
  }

  &__messages {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    // Keep momentum scrolling on iOS and stop scroll from chaining to the
    // page behind the panel on touch devices.
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    padding: 18px 16px;
    background: #fcfdff;
  }

  &__empty {
    text-align: center;
    padding: 32px 8px;
    position: relative;
  }

  &__empty-glow {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 200px;
    height: 200px;
    background: radial-gradient(circle, rgba(91, 115, 232, 0.18) 0%, rgba(139, 92, 246, 0.05) 50%, transparent 70%);
    pointer-events: none;
    z-index: 0;
  }

  &__empty-title {
    position: relative;
    z-index: 1;
    font-size: 1.15rem;
    font-weight: 700;
    color: #1e2b45;
    margin: 32px 0 6px;
    background: linear-gradient(135deg, #5b73e8 0%, #8b5cf6 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  &__empty-text {
    position: relative;
    z-index: 1;
    font-size: 0.85rem;
    color: #5a6782;
    margin: 0 auto 16px;
    line-height: 1.55;
    max-width: 340px;
  }

  &__empty-notes {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-width: 360px;
    margin: 0 auto 20px;
    text-align: left;
  }

  &__empty-note {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 10px 12px;
    background: #f6f8fd;
    border: 1px solid #e9ecf3;
    border-radius: 10px;
  }

  &__empty-note-icon {
    flex-shrink: 0;
    font-size: 0.95rem;
    line-height: 1.4;
  }

  &__empty-note-text {
    font-size: 0.78rem;
    color: #5a6782;
    line-height: 1.5;

    strong { color: #1e2b45; }
  }

  &__suggest-label {
    position: relative;
    z-index: 1;
    text-align: left;
    font-size: 0.68rem;
    font-weight: 700;
    color: #96a5be;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    margin: 4px 4px 8px;
  }

  &__suggestions {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__suggestion {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 14px;
    background: #fff;
    border: 1px solid #e9ecf3;
    border-radius: 10px;
    cursor: pointer;
    text-align: left;
    color: #1e2b45;
    transition: border-color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;

    &:hover {
      border-color: #c7d2f7;
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(91, 115, 232, 0.12);
      background: linear-gradient(180deg, #ffffff 0%, #fafbff 100%);
    }
  }

  &__suggestion-icon {
    font-size: 1.05rem;
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, rgba(91, 115, 232, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%);
    border-radius: 6px;
  }

  &__suggestion-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  &__suggestion-text {
    font-size: 0.85rem;
    font-weight: 500;
    line-height: 1.35;
    color: #1e2b45;
  }

  &__suggestion-hint {
    font-size: 0.72rem;
    color: #96a5be;
    line-height: 1.35;
  }

  &__thread {
    display: flex;
    flex-direction: column;
  }

  &__composer {
    position: relative;
    padding: 12px 16px 14px;
    border-top: 1px solid #e9ecf3;
    background: #ffffff;
    flex-shrink: 0;
  }

  // @-mention account autocomplete, floating above the composer.
  &__mention {
    position: absolute;
    left: 12px;
    right: 12px;
    bottom: 100%;
    margin-bottom: 6px;
    max-height: 280px;
    overflow-y: auto;
    background: #ffffff;
    border: 1px solid #e9ecf3;
    border-radius: 10px;
    box-shadow: 0 -8px 24px rgba(30, 44, 80, 0.14);
    z-index: 3;
  }

  &__mention-head {
    padding: 8px 12px 4px;
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #96a5be;
  }

  &__mention-empty {
    padding: 8px 12px 12px;
    font-size: 0.78rem;
    color: #96a5be;
  }

  &__mention-item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 8px 12px;
    background: transparent;
    border: 0;
    cursor: pointer;
    text-align: left;

    &--active { background: #eef0fd; }
  }

  &__mention-type {
    flex-shrink: 0;
    font-size: 0.6rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 1px 6px;
    border-radius: 999px;
    background: #eef0fd;
    color: #3858e7;

    &--seller { background: #e7f7ef; color: #14804a; }
  }

  &__mention-name {
    min-width: 0;
    font-size: 0.82rem;
    font-weight: 600;
    color: #1e2b45;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__mention-id {
    margin-left: auto;
    flex-shrink: 0;
    max-width: 45%;
    font-family: 'SF Mono', 'Menlo', 'Consolas', monospace;
    font-size: 0.66rem;
    color: #96a5be;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  // Queued prompts sit directly above the composer in submit order. The border
  // is dashed, against the solid streaming bar below it, so "waiting" and
  // "running" are told apart at a glance.
  &__queue {
    margin-bottom: 8px;
    padding: 6px 8px;
    background: #f6f8fd;
    border: 1px dashed #c7d2f7;
    border-radius: 8px;
  }

  &__queue-head {
    margin-bottom: 4px;
    color: #7b88a8;
    font-size: 0.68rem;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }

  &__queue-item {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    padding: 3px 0;
  }

  &__queue-index {
    flex-shrink: 0;
    width: 15px;
    height: 15px;
    margin-top: 1px;
    background: #dde3f7;
    border-radius: 50%;
    color: #3858e7;
    font-size: 0.64rem;
    font-weight: 600;
    line-height: 15px;
    text-align: center;
  }

  // Clamped to two lines: enough to recognise the prompt. The whole thing is
  // still queued, only the preview is short.
  &__queue-text {
    display: -webkit-box;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    color: #4a5878;
    font-size: 0.76rem;
    line-height: 1.4;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  &__queue-drop {
    flex-shrink: 0;
    padding: 0 2px;
    background: none;
    border: 0;
    color: #9aa5c0;
    cursor: pointer;

    &:hover { color: #f87171; }
  }

  &__streaming-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 10px;
    background: #eef0fd;
    border: 1px solid #c7d2f7;
    border-radius: 8px;
    margin-bottom: 10px;
    font-size: 0.78rem;
  }

  &__streaming-text {
    color: #3858e7;
    font-weight: 500;
  }

  &__stop-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: #fff;
    border: 1px solid #c7d2f7;
    color: #3858e7;
    border-radius: 6px;
    padding: 3px 8px;
    cursor: pointer;
    font-size: 0.75rem;
    font-weight: 500;

    &:hover { background: #eef0fd; }
  }

  &__input-wrap {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    background: #f6f8fd;
    border: 1px solid #e9ecf3;
    border-radius: 12px;
    padding: 8px 8px 8px 12px;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:focus-within {
      border-color: #5b73e8;
      box-shadow: 0 0 0 3px rgba(91, 115, 232, 0.15);
    }
  }

  &__input {
    flex: 1;
    border: 0;
    outline: 0;
    background: transparent;
    resize: none;
    font-family: inherit;
    font-size: 0.875rem;
    line-height: 1.45;
    color: #1e2b45;
    max-height: 160px;
    overflow-y: auto;

    &::placeholder { color: #96a5be; }
    &:disabled { color: #96a5be; cursor: not-allowed; }
  }

  &__send-btn {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: linear-gradient(135deg, #5b73e8 0%, #4a60d4 100%);
    border: 0;
    color: #fff;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: opacity 0.15s ease, transform 0.15s ease;

    &:hover:not(:disabled) { transform: translateY(-1px); }
    &:disabled { opacity: 0.4; cursor: not-allowed; }
  }

  &__send-spinner {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: #fff;
    animation: ai-spin 0.7s linear infinite;
  }

  &__hint {
    font-size: 0.68rem;
    color: #96a5be;
    margin-top: 6px;
    text-align: center;
    letter-spacing: 0.1px;

    kbd {
      background: #f2f4f9;
      border: 1px solid #e9ecf3;
      border-radius: 4px;
      padding: 1px 4px;
      font-family: 'SF Mono', 'Menlo', 'Consolas', monospace;
      font-size: 0.65rem;
      color: #5a6782;
    }
  }

  // --- Saved chat history ---------------------------------------------------
  // Full-panel overlay so it has exactly one scroll region (its own list) and
  // never fights the message scroll underneath.
  &__history {
    position: absolute;
    inset: 0;
    z-index: 2;
    display: flex;
    flex-direction: column;
    background: #ffffff;
  }

  &__history-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 14px 16px;
    border-bottom: 1px solid #e9ecf3;
    flex-shrink: 0;
  }

  &__history-title {
    font-size: 0.95rem;
    font-weight: 700;
    color: #1e2b45;
  }

  &__history-head-actions {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
  }

  &__history-new {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background: linear-gradient(135deg, #5b73e8 0%, #4a60d4 100%);
    border: 0;
    color: #fff;
    border-radius: 8px;
    padding: 6px 12px;
    font-size: 0.78rem;
    font-weight: 600;
    cursor: pointer;
    transition: transform 0.15s ease;

    &:hover { transform: translateY(-1px); }
  }

  &__history-list {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    padding: 12px;
  }

  &__history-empty {
    color: #96a5be;
    font-size: 0.85rem;
    text-align: center;
    line-height: 1.55;
    padding: 40px 20px;
  }

  &__history-item {
    display: flex;
    align-items: stretch;
    border: 1px solid #e9ecf3;
    border-radius: 10px;
    margin-bottom: 8px;
    background: #fff;
    overflow: hidden;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:hover {
      border-color: #c7d2f7;
      box-shadow: 0 4px 12px rgba(91, 115, 232, 0.1);
    }

    &--active { border-color: #5b73e8; background: #f6f8fd; }
  }

  &__history-open {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 3px;
    padding: 10px 12px;
    background: transparent;
    border: 0;
    text-align: left;
    cursor: pointer;
  }

  &__history-item-title {
    font-size: 0.85rem;
    font-weight: 600;
    color: #1e2b45;
    line-height: 1.35;
    width: 100%;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__history-item-meta {
    font-size: 0.72rem;
    color: #96a5be;
  }

  &__history-del {
    flex-shrink: 0;
    width: 42px;
    background: transparent;
    border: 0;
    border-left: 1px solid #f0f2f7;
    color: #96a5be;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s ease, color 0.15s ease;

    &:hover { background: #fef2f2; color: #dc2626; }
  }

  // --- Compact phone layout -------------------------------------------------
  // Tighten chrome and drop desktop-only affordances so the header never
  // overflows on narrow screens. The brand text already truncates; this just
  // claws back the padding and hides the keyboard hint row, which is moot on
  // a touch keyboard.
  @media (max-width: 600px) {
    &__header { padding: 10px 12px; }
    &__messages { padding: 14px 12px; }
    &__composer { padding: 10px 12px 12px; }
    &__history-head { padding: 10px 12px; }
    &__title { font-size: 0.9rem; }
    &__icon-btn { width: 30px; height: 30px; }
    &__icon-btn--maximize { display: none; }
    &__hint { display: none; }
  }
}

// Terminal mode — always dark, monospace, tighter padding, slightly different
// chrome. Activates regardless of the global .c-dark-theme toggle.
.ai-panel--cli {
  background: #0f1320;
  border-left-color: #1f2540;
  box-shadow: -16px 0 48px rgba(0, 0, 0, 0.45), -4px 0 16px rgba(0, 0, 0, 0.3);

  .ai-panel__header {
    background: linear-gradient(180deg, #181d2f 0%, #0f1320 100%);
    border-bottom-color: #1f2540;
  }
  .ai-panel__title { color: #e5edf8; }
  .ai-panel__subtitle { color: #6b7fa8; }
  .ai-panel__icon-btn {
    color: #adbacf;
    &:hover { background: #181d2f; color: #e5edf8; }
  }
  .ai-panel__messages {
    background: #0f1320;
    padding: 14px 18px;
    font-family: 'SF Mono', 'JetBrains Mono', 'Menlo', 'Consolas', monospace;
  }
  .ai-panel__empty-title { color: #e5edf8; background: none; -webkit-text-fill-color: initial; }
  .ai-panel__empty-text { color: #adbacf; }
  .ai-panel__empty-note {
    background: #181d2f;
    border-color: #1f2540;
  }
  .ai-panel__empty-note-text {
    color: #adbacf;
    strong { color: #e5edf8; }
  }
  .ai-panel__suggest-label { color: #6b7fa8; }
  .ai-panel__suggestion {
    background: #181d2f;
    border-color: #1f2540;
    color: #e5edf8;
    font-family: inherit;
    &:hover { border-color: #4c6aff; background: #1d2236; }
  }
  .ai-panel__suggestion-text { color: #e5edf8; }
  .ai-panel__suggestion-hint { color: #6b7fa8; }
  .ai-panel__suggestion-icon {
    background: rgba(126, 169, 245, 0.12);
  }
  .ai-panel__composer {
    background: #0f1320;
    border-top-color: #1f2540;
  }
  .ai-panel__input-wrap {
    background: #181d2f;
    border-color: #1f2540;
    border-radius: 4px;
    &:focus-within {
      border-color: #6ee7b7;
      box-shadow: 0 0 0 3px rgba(110, 231, 183, 0.15);
    }
    &::before {
      content: '$';
      color: #6ee7b7;
      font-family: 'SF Mono', 'JetBrains Mono', 'Menlo', 'Consolas', monospace;
      font-weight: 700;
      align-self: center;
      margin-right: 8px;
    }
  }
  .ai-panel__input {
    color: #e5edf8;
    font-family: 'SF Mono', 'JetBrains Mono', 'Menlo', 'Consolas', monospace;
    font-size: 0.8rem;
    &::placeholder { color: #6b7fa8; }
  }
  .ai-panel__send-btn {
    background: #6ee7b7;
    color: #0f1320;
    border-radius: 4px;
  }
  .ai-panel__hint {
    color: #6b7fa8;
    kbd {
      background: #181d2f;
      border-color: #1f2540;
      color: #adbacf;
    }
  }
  .ai-panel__streaming-bar {
    background: rgba(110, 231, 183, 0.08);
    border-color: rgba(110, 231, 183, 0.3);
  }
  .ai-panel__streaming-text { color: #6ee7b7; }
  .ai-panel__stop-btn {
    background: transparent;
    border-color: rgba(110, 231, 183, 0.3);
    color: #6ee7b7;
    &:hover { background: rgba(110, 231, 183, 0.1); }
  }
  .ai-panel__mention {
    background: #181d2f;
    border-color: #1f2540;
    box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.5);
    border-radius: 4px;
  }
  .ai-panel__mention-head { color: #6b7fa8; }
  .ai-panel__mention-empty { color: #6b7fa8; }
  .ai-panel__mention-item--active { background: #1d2236; }
  .ai-panel__mention-name { color: #e5edf8; }
  .ai-panel__mention-id { color: #6b7fa8; }
  .ai-panel__mention-type {
    background: rgba(110, 231, 183, 0.12);
    color: #6ee7b7;
    &--seller { background: rgba(126, 169, 245, 0.12); color: #7ea9f5; }
  }
  .ai-panel__icon-btn--active { background: #2c3254; color: #6ee7b7; }
  .ai-panel__history { background: #0f1320; }
  .ai-panel__history-head { border-bottom-color: #1f2540; }
  .ai-panel__history-title { color: #e5edf8; }
  .ai-panel__history-new { background: #6ee7b7; color: #0f1320; border-radius: 4px; }
  .ai-panel__history-empty { color: #6b7fa8; }
  .ai-panel__history-item {
    background: #181d2f;
    border-color: #1f2540;
    border-radius: 4px;
    &:hover { border-color: #4c6aff; box-shadow: none; }
    &--active { border-color: #6ee7b7; background: #1d2236; }
  }
  .ai-panel__history-item-title { color: #e5edf8; }
  .ai-panel__history-item-meta { color: #6b7fa8; }
  .ai-panel__history-del {
    border-left-color: #1f2540;
    color: #6b7fa8;
    &:hover { background: rgba(248, 113, 113, 0.12); color: #f87171; }
  }
}

.ai-panel-slide-enter-active,
.ai-panel-slide-leave-active {
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}
.ai-panel-slide-enter,
.ai-panel-slide-leave-to {
  transform: translateX(100%);
}

@keyframes ai-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(0.85); }
}

@keyframes ai-spin {
  to { transform: rotate(360deg); }
}
</style>

<style lang="scss">
.c-dark-theme .ai-panel {
  background: #1b1f33;
  border-left-color: #2d3252;

  &__header { background: linear-gradient(180deg, #22263a 0%, #1b1f33 100%); border-bottom-color: #2d3252; }
  &__title { color: #d4dff0; }
  &__subtitle { color: #6b7fa8; }
  &__icon-btn { color: #adbacf; &:hover { background: #252942; color: #d4dff0; } }
  &__messages { background: #1b1f33; }
  &__empty-title { color: #d4dff0; }
  &__empty-text { color: #adbacf; }
  &__empty-note { background: #22263a; border-color: #2d3252; }
  &__empty-note-text { color: #adbacf; strong { color: #d4dff0; } }
  &__suggest-label { color: #6b7fa8; }
  &__suggestion {
    background: #22263a;
    border-color: #2d3252;
    color: #d4dff0;
    &:hover { border-color: #4c6aff; box-shadow: 0 6px 16px rgba(91, 115, 232, 0.25); background: linear-gradient(180deg, #22263a 0%, #252942 100%); }
  }
  &__suggestion-text { color: #d4dff0; }
  &__suggestion-hint { color: #6b7fa8; }
  &__suggestion-icon { background: linear-gradient(135deg, rgba(126, 169, 245, 0.18) 0%, rgba(139, 92, 246, 0.18) 100%); }
  &__composer { background: #1b1f33; border-top-color: #2d3252; }
  &__input-wrap {
    background: #22263a;
    border-color: #2d3252;
    &:focus-within { border-color: #5b73e8; box-shadow: 0 0 0 3px rgba(91, 115, 232, 0.25); }
  }
  &__input { color: #d4dff0; &::placeholder { color: #6b7fa8; } }
  &__hint { color: #6b7fa8; kbd { background: #2c3254; border-color: #2d3252; color: #adbacf; } }
  &__streaming-bar { background: #2c3254; border-color: #4c6aff; }
  &__streaming-text { color: #7ea9f5; }
  &__stop-btn { background: #1b1f33; border-color: #4c6aff; color: #7ea9f5; }
  &__queue { background: #22263a; border-color: #3d4470; }
  &__queue-head { color: #6b7fa8; }
  &__queue-index { background: #2c3254; color: #7ea9f5; }
  &__queue-text { color: #a8b6d4; }
  &__queue-drop { color: #6b7fa8; }
  &__icon-btn--active { background: #2c3254; color: #7ea9f5; }
  &__history { background: #1b1f33; }
  &__history-head { border-bottom-color: #2d3252; }
  &__history-title { color: #d4dff0; }
  &__history-empty { color: #6b7fa8; }
  &__history-item {
    background: #22263a;
    border-color: #2d3252;
    &:hover { border-color: #4c6aff; box-shadow: 0 4px 12px rgba(91, 115, 232, 0.25); }
    &--active { border-color: #5b73e8; background: #252942; }
  }
  &__history-item-title { color: #d4dff0; }
  &__history-item-meta { color: #6b7fa8; }
  &__history-del {
    border-left-color: #2d3252;
    color: #6b7fa8;
    &:hover { background: #3a1818; color: #f87171; }
  }
}
</style>
